import { motion } from 'framer-motion'

export type BillingCycle = 'monthly' | 'annual'
export type PlanTierId = 'free' | 'developer' | 'pro' | 'enterprise'

interface PlanSwitchProps {
  activePlan: PlanTierId
  onPlanChange: (plan: PlanTierId) => void
  billingCycle: BillingCycle
  onBillingChange: (cycle: BillingCycle) => void
}

const plans: { id: PlanTierId; label: string }[] = [
  { id: 'free', label: 'Free' },
  { id: 'developer', label: 'Developer' },
  { id: 'pro', label: 'Pro' },
  { id: 'enterprise', label: 'Enterprise' },
]

export default function PriceSwitch({
  activePlan,
  onPlanChange,
  billingCycle,
  onBillingChange,
}: PlanSwitchProps) {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap">
      {/* 4-Plan Segmented Switcher */}
      <div className="inline-flex items-center p-1 sm:p-1.5 rounded-full bg-[#150d08] border border-[#3a2214] shadow-xl relative backdrop-blur-sm">
        {plans.map((plan) => {
          const isActive = activePlan === plan.id
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onPlanChange(plan.id)}
              className={`relative z-10 px-3.5 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors duration-200 cursor-pointer flex items-center gap-1.5 ${
                isActive ? 'text-white' : 'text-[#c9a894] hover:text-white'
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="plan-switch-active"
                  className="absolute inset-0 rounded-full bg-[#0a0603] border border-primary/60 shadow-md"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10">{plan.label}</span>
            </button>
          )
        })}
      </div>

      {/* Monthly / Annual Billing Toggle */}
      <div className="inline-flex items-center p-1 sm:p-1.5 rounded-full bg-[#150d08] border border-[#3a2214] shadow-xl relative backdrop-blur-sm">
        <button
          type="button"
          onClick={() => onBillingChange('monthly')}
          className={`relative z-10 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors duration-200 cursor-pointer ${
            billingCycle === 'monthly'
              ? 'text-white'
              : 'text-[#c9a894] hover:text-white'
          }`}
        >
          {billingCycle === 'monthly' && (
            <motion.div
              layoutId="billing-switch-active"
              className="absolute inset-0 rounded-full bg-[#0a0603] border border-primary/50 shadow-md"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          <span className="relative z-10">Monthly</span>
        </button>

        <button
          type="button"
          onClick={() => onBillingChange('annual')}
          className={`relative z-10 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors duration-200 cursor-pointer flex items-center gap-2 ${
            billingCycle === 'annual'
              ? 'text-white'
              : 'text-[#c9a894] hover:text-white'
          }`}
        >
          {billingCycle === 'annual' && (
            <motion.div
              layoutId="billing-switch-active"
              className="absolute inset-0 rounded-full bg-[#0a0603] border border-primary/50 shadow-md"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          <span className="relative z-10">Annual</span>
          <span className="relative z-10 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-primary text-white leading-none shadow-sm">
            -20%
          </span>
        </button>
      </div>
    </div>
  )
}
